<template>
  <div class="home-shell c-bg-ambient">
    <!-- Unified header: avatar + greeting + LINE status + bell -->
    <header class="hh-header">
      <button
        class="hh-avatar"
        :style="avatarStyle"
        type="button"
        aria-label="โปรไฟล์ของฉัน"
        @click="goProfile"
      >
        <img
          v-if="liffProfile?.pictureUrl"
          :src="liffProfile.pictureUrl"
          :alt="liffProfile.displayName"
        />
        <span v-else class="hh-avatar-initial">{{ initial }}</span>
        <span class="hh-avatar-dot" title="เชื่อมต่อกับ LINE แล้ว" />
      </button>
      <div class="hh-greet">
        <div class="hh-greet-line">{{ greetingByTime }}</div>
        <div class="hh-name">{{ displayName }}</div>
        <div class="hh-sub" v-if="hospName">{{ hospName }}</div>
        <div class="hh-sub" v-else>สุขภาพดี เริ่มต้นที่ดูแลตัวเอง</div>
      </div>
      <button class="hh-bell" type="button" @click="onBell">
        <el-icon :size="22"><Bell /></el-icon>
        <span v-if="unreadCount > 0" class="hh-badge-count">{{
          unreadCount > 9 ? "9+" : unreadCount
        }}</span>
      </button>
    </header>

    <NotificationDropdown :open="notifOpen" @close="notifOpen = false" />

    <main class="hh-body">
      <!-- Banners Carousel (Hero + Promo) -->
      <div class="hh-banner-carousel">
        <div class="hh-banner-carousel-inner">
          <!-- 1. Primary CTA (Hero Banner Slide) -->
          <section class="hh-hero hh-banner-slide">
            <div class="hh-hero-left">
              <div class="hh-hero-pill">
                <span class="hh-pill-spark">✨</span> พร้อมให้บริการ
              </div>
              <h2 class="hh-hero-title">ปรึกษาแพทย์ออนไลน์</h2>
              <div class="hh-hero-sub">
                หาคุณหมอใจดี ที่อยากปรึกษาให้คุณ ทุกเวลา
              </div>
              <button type="button" class="hh-hero-btn" @click="onConsult">
                ปรึกษาแพทย์เลย
              </button>
              <div class="hh-hero-status">
                <span class="hh-hero-status-dot" />
                <span>{{ doctorStatusText }}</span>
              </div>
            </div>
            <div class="hh-hero-right">
              <div class="hh-hero-bubbles">
                <div class="hh-hero-bubble">
                  <el-icon :size="14"><ChatLineRound /></el-icon>
                </div>
                <div class="hh-hero-bubble">
                  <el-icon :size="14"><VideoCamera /></el-icon>
                </div>
              </div>
              <div class="hh-hero-doctor">
                <span class="hh-hero-doctor-emoji">👩‍⚕️</span>
                <span class="hh-hero-doctor-wave">👋</span>
              </div>
            </div>
            <!-- Page indicator dots -->
            <div class="hh-hero-dots">
              <span
                class="hh-hero-dot"
                :class="{ active: currentSlide === 0 }"
              />
              <span
                v-for="(_, i) in heroCampaigns"
                :key="i"
                class="hh-hero-dot"
                :class="{ active: currentSlide === i + 1 }"
              />
            </div>
          </section>

          <!-- Dynamic Promotional Slides -->
          <section
            v-for="promo in heroCampaigns"
            :key="promo.id"
            class="hh-promo hh-banner-slide"
            :style="{
              backgroundImage: promo.imageUrl
                ? `url(${getApiUrl(promo.imageUrl)})`
                : 'none',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              display: 'flex',
              alignItems: 'flex-end',
              padding: '20px',
            }"
            @click="onPromotionClick(promo)"
          >
            <div
              class="hh-promo-text"
              style="
                background: rgba(255, 255, 255, 0.9);
                padding: 12px;
                border-radius: 8px;
                width: 100%;
              "
            >
              <span class="hh-promo-pill">โปรโมชั่น</span>
              <h3 class="hh-promo-title" style="color: #1e3a8a">
                {{ promo.title }}
              </h3>
              <div
                v-if="promo.description"
                class="hh-promo-tagline"
                style="color: #475569"
              >
                {{ promo.description }}
              </div>
            </div>
          </section>
        </div>
      </div>

      <!-- 3. Service tiles (Grid Menu) — unique actions only, bottom nav handles ประวัติ/ผลตรวจ/แพ็กเกจ -->
      <section class="hh-tiles">
        <button class="hh-tile" type="button" @click="onTile('appoint')">
          <div class="hh-tile-icon" style="background: #fef3c7; color: #d97706">
            <el-icon :size="22"><Calendar /></el-icon>
          </div>
          <div class="hh-tile-label">นัดหมาย</div>
        </button>
        <button class="hh-tile" type="button" @click="onTile('vaccine')">
          <div class="hh-tile-icon" style="background: #e0f2fe; color: #0284c7">
            <el-icon :size="22"><FirstAidKit /></el-icon>
          </div>
          <div class="hh-tile-label">แพ็กเกจ/วัคซีน</div>
        </button>
        <button class="hh-tile" type="button" @click="onTile('family')">
          <div class="hh-tile-icon" style="background: #f3e8ff; color: #9333ea">
            <el-icon :size="22"><User /></el-icon>
          </div>
          <div class="hh-tile-label">ครอบครัว</div>
        </button>
        <button class="hh-tile" type="button" @click="onTile('receipt')">
          <div class="hh-tile-icon" style="background: #d1fae5; color: #059669">
            <el-icon :size="22"><Document /></el-icon>
          </div>
          <div class="hh-tile-label">ใบเสร็จ</div>
        </button>
      </section>

      <!-- 3. Upcoming Appointment / Empty State -->
      <section v-if="me?.upcomingAppointment" class="hh-recent">
        <div class="hh-section-title">
          <span>นัดที่จะถึง</span>
          <button
            type="button"
            class="hh-section-action"
            @click="onTile('appoint')"
          >
            ดูทั้งหมด <span class="hh-arrow">›</span>
          </button>
        </div>
        <div class="c-card hh-recent-card">
          <div class="hh-recent-avatar">
            <el-icon :size="20" color="#3b82f6"><Calendar /></el-icon>
          </div>
          <div class="hh-recent-info">
            <div class="hh-recent-name">{{ appointmentTypeLabel }}</div>
            <div class="hh-recent-spec">{{ me.fname }} {{ me.lname }}</div>
            <div class="hh-recent-meta">
              <el-icon :size="12"><Calendar /></el-icon>
              <span>{{ formattedDate }}</span>
            </div>
          </div>
          <div class="hh-recent-side">
            <span
              class="hh-recent-status-pill"
              :class="`status-${me.upcomingAppointment.status.toLowerCase()}`"
            >
              {{ appointmentStatusLabel }}
            </span>
            <button
              type="button"
              class="hh-recent-btn-summary"
              @click="onTile('appoint')"
            >
              ดูรายละเอียด <span class="hh-recent-arrow">›</span>
            </button>
          </div>
        </div>
      </section>

      <section v-else class="hh-empty c-card">
        <div class="hh-empty-icon">
          <span>📅</span>
        </div>
        <div class="hh-empty-text">
          ยังไม่มีการนัดหมาย<br /><span class="hh-empty-sub">เริ่มนัดหมายแพทย์ เพื่อรับคำปรึกษาออนไลน์</span>
        </div>
        <el-button
          type="primary"
          class="c-btn-primary"
          @click="onTile('appoint')"
        >
          นัดหมายเลย
        </el-button>
      </section>
    </main>

    <CustomerBottomNav active="home" />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from "vue";
import { useRouter } from "vue-router";
import dayjs from "dayjs";
import {
  Bell,
  Calendar,
  User,
  ChatLineRound,
  VideoCamera,
  Document,
  FirstAidKit,
} from "@element-plus/icons-vue";
import { ElMessage } from "element-plus";
import { useLiff } from "@/composables/useLiff";
import { useCustomerMe } from "@/composables/useCustomerMe";
import { useNotifications } from "@/composables/useNotifications";
import { doctorApi } from "@/api/doctor.api";
import { publicApi } from "@/api/public.api";
import { usePromotion } from "@/composables/usePromotion";
import CustomerBottomNav from "@/components/customer/CustomerBottomNav.vue";
import NotificationDropdown from "@/components/customer/NotificationDropdown.vue";
import { heroBannerApi } from "@/api/heroBanner.api";
import type { HeroBannerView } from "@/api/heroBannerAdmin.api";

const router = useRouter();
let isMounted = false;
const { profile: liffProfile, init: initLiff, status: liffStatus } = useLiff();
const { me, load: loadMe } = useCustomerMe();
const { unreadCount } = useNotifications();
const { campaigns, refreshAll: loadCampaigns } = usePromotion();
const notifOpen = ref(false);
const hospName = ref("");

const heroBanners = ref<HeroBannerView[]>([]);

const heroCampaigns = computed(() => {
  // 1. Get explicit hero banners
  const banners = heroBanners.value.map((b) => ({
    id: b.id,
    title: b.title,
    description: null,
    imageUrl: b.imageUrl,
    linkUrl: b.linkUrl,
  }));

  // 2. Get promotions that have images AND don't use a heroBannerId (to avoid duplicates if they selected one)
  const promos = campaigns.value
    .filter((c) => c.imageUrl && !c.heroBannerId)
    .slice(0, 6)
    .map((c) => ({
      id: c.id,
      title: c.title,
      description: c.description,
      imageUrl: c.imageUrl,
      linkUrl: null,
    }));

  return [...banners, ...promos];
});

const currentSlide = ref(0); // For dot indicators

function getApiUrl(path: string): string {
  if (!path) return '';
  if (path.startsWith("http")) return path;
  const baseUrl =
    import.meta.env.VITE_API_URL ||
    import.meta.env.VITE_API_BASE_URL ||
    "/api/v1";
    
  const base = baseUrl.replace(/\/$/, '');
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${base}${normalizedPath}`;
}

function onPromotionClick(promo: any) {
  if (promo.linkUrl) {
    window.open(promo.linkUrl, '_blank');
  } else {
    router.push({ name: "customer-vaccine", query: { category: "PROMOTION" } });
  }
}

const doctorCount = ref<number | null>(null);
const doctorStatusText = computed(() => {
  if (doctorCount.value == null) return "พร้อมให้คำปรึกษา";
  if (doctorCount.value === 0) return "แพทย์ไม่พร้อมในเวลานี้";
  return `พร้อมให้คำปรึกษา ${doctorCount.value} ท่าน`;
});

const greetingByTime = computed(() => {
  const h = new Date().getHours();
  if (h < 12) return "สวัสดีตอนเช้า";
  if (h < 17) return "สวัสดีตอนบ่าย";
  return "สวัสดีตอนเย็น";
});

const displayName = computed(() => {
  if (me.value?.fname || me.value?.lname) {
    return `คุณ${me.value.fname ?? ""} ${me.value.lname ?? ""}`.trim();
  }
  return liffProfile.value?.displayName ?? "ผู้ใช้";
});

const initial = computed(() => {
  const name = me.value?.fname ?? liffProfile.value?.displayName ?? "?";
  return [...name][0]?.toUpperCase() ?? "?";
});

const avatarStyle = computed(() => {
  const seed = liffProfile.value?.userId ?? "0";
  const hue = (seed.charCodeAt(2) ?? 200) % 360;
  return {
    background: `linear-gradient(135deg, hsl(${hue}, 70%, 65%), hsl(${(hue + 40) % 360}, 70%, 55%))`,
  };
});

const formattedDate = computed(() =>
  me.value?.upcomingAppointment
    ? dayjs(me.value.upcomingAppointment.scheduledAt).format(
        "DD MMM YYYY HH:mm",
      )
    : "",
);

const APPOINTMENT_STATUS_LABELS: Record<string, string> = {
  WAITING_MED_RECORD: "รอยืนยัน",
  PENDING: "รอยืนยัน",
  CONFIRMED: "ยืนยันแล้ว",
  INPROGRESS: "กำลังตรวจ",
};
const appointmentStatusLabel = computed(
  () =>
    APPOINTMENT_STATUS_LABELS[me.value?.upcomingAppointment?.status ?? ""] ??
    "รอนัด",
);

const APPOINTMENT_TYPE_LABELS: Record<string, string> = {
  TELEMED: "ปรึกษาแพทย์ออนไลน์",
  IN_PERSON: "พบแพทย์ที่โรงพยาบาล",
  FOLLOWUP: "นัดติดตามอาการ",
  VACCINE: "นัดหมายแพ็กเกจ/บริการ",
};
const appointmentTypeLabel = computed(
  () =>
    APPOINTMENT_TYPE_LABELS[
      me.value?.upcomingAppointment?.appointmentType ?? ""
    ] ?? "นัดหมาย",
);

async function load() {
  const result = await loadMe(true);
  if (!isMounted) return;
  if (!result) {
    ElMessage.warning("กรุณาเข้าสู่ระบบใหม่");
    bounceToRegister();
    return;
  }
  let hospId =
    result.upcomingAppointment?.hospId ??
    sessionStorage.getItem("customer:hospId") ??
    result.hospId;
  if (!hospId || hospId === "undefined" || hospId === "null") {
    hospId = result.hospId || "11854";
  }
  if (hospId) {
    sessionStorage.setItem("customer:hospId", hospId);
    try {
      const doctors = await doctorApi.list(hospId);
      doctorCount.value = doctors.filter((d) => d.isOnline).length;
      const c = await publicApi.getDefaultContract(hospId);
      hospName.value = c.name;
    } catch (e) {
      doctorCount.value = null;
    }
  }
  await loadCampaigns();
  try {
    heroBanners.value = await heroBannerApi.listBanners(Number(hospId || 11457));
  } catch (e) {
    // fallback gracefully
  }

  // Track scroll for dots and handle drag-to-scroll for desktop
  setTimeout(() => {
    const carouselInner = document.querySelector(".hh-banner-carousel-inner");
    const carouselWrapper = document.querySelector(".hh-banner-carousel");
    
    if (carouselInner && carouselWrapper) {
      // 1. Update dot indicators on scroll
      carouselWrapper.addEventListener("scroll", () => {
        const slideWidth = carouselInner.clientWidth;
        currentSlide.value = Math.round(carouselWrapper.scrollLeft / slideWidth);
      });

      // 2. Drag-to-scroll logic
      let isDown = false;
      let startX: number;
      let scrollLeft: number;

      carouselWrapper.addEventListener('mousedown', (e) => {
        const ev = e as MouseEvent;
        isDown = true;
        (carouselWrapper as HTMLElement).style.cursor = 'grabbing';
        (carouselWrapper as HTMLElement).style.scrollSnapType = 'none'; // Disable snap during drag
        startX = ev.pageX - (carouselWrapper as HTMLElement).offsetLeft;
        scrollLeft = carouselWrapper.scrollLeft;
      });

      carouselWrapper.addEventListener('mouseleave', () => {
        isDown = false;
        (carouselWrapper as HTMLElement).style.cursor = 'default';
        (carouselWrapper as HTMLElement).style.scrollSnapType = 'x mandatory'; // Re-enable snap
      });

      carouselWrapper.addEventListener('mouseup', () => {
        isDown = false;
        (carouselWrapper as HTMLElement).style.cursor = 'default';
        (carouselWrapper as HTMLElement).style.scrollSnapType = 'x mandatory'; // Re-enable snap
      });

      carouselWrapper.addEventListener('mousemove', (e) => {
        if (!isDown) return;
        const ev = e as MouseEvent;
        ev.preventDefault(); // Prevent text selection
        const x = ev.pageX - (carouselWrapper as HTMLElement).offsetLeft;
        const walk = (x - startX) * 2; // Scroll speed multiplier
        carouselWrapper.scrollLeft = scrollLeft - walk;
      });
    }
  }, 500);
}

function bounceToRegister(): void {
  if (!isMounted) return;
  const lastKey = sessionStorage.getItem("register:lastContractKey");
  if (lastKey) {
    router.replace({
      name: "customer-register",
      params: { contractKey: lastKey },
    });
  } else {
    router.replace({ name: "not-found" });
  }
}

function onBell(): void {
  notifOpen.value = !notifOpen.value;
}

function onConsult(): void {
  router.push({ name: "customer-appointment", query: { type: "telemed" } });
}

function onTile(kind: string): void {
  switch (kind) {
    case "appoint":
      router.push({ name: "customer-appointment" });
      return;
    case "history":
      router.push({ name: "customer-history" });
      return;
    case "vaccine":
      router.push({ name: "customer-vaccine" });
      return;
    case "health-report":
      router.push({ name: "customer-health-report" });
      return;
    case "promotions":
      router.push({ name: "customer-vaccine", query: { category: "PROMOTION" } });
      return;
    case "receipt":
      router.push({ name: "customer-receipt" });
      return;
    case "profile":
      router.push({ name: "customer-profile" });
      return;
    case "family":
      router.push({ name: "customer-family" });
      return;
    default:
      ElMessage.info("ฟีเจอร์นี้กำลังพัฒนา");
  }
}

function goProfile(): void {
  router.push({ name: "customer-profile" });
}

onMounted(async () => {
  isMounted = true;
  if (liffStatus.value !== "ready") {
    await initLiff();
  }
  await load();
});

onUnmounted(() => {
  isMounted = false;
});
</script>

<style scoped>
.home-shell {
  height: 100vh;
  height: 100dvh;
  max-width: 440px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
}
@media (min-width: 480px) {
  .home-shell {
    box-shadow: 0 0 36px rgba(59, 130, 246, 0.1);
  }
}

/* ----- Header ----- */
.hh-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: max(12px, env(safe-area-inset-top)) 16px 12px;
}
.hh-avatar {
  flex: 0 0 auto;
  position: relative;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: visible;
  border: 1px solid #e2e8f0;
  padding: 0;
  cursor: pointer;
  font-family: inherit;
}
.hh-avatar img {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
}
.hh-avatar-initial {
  font-size: 18px;
  font-weight: 700;
  color: #ffffff;
}
.hh-avatar-dot {
  position: absolute;
  bottom: -2px;
  right: -2px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #06c755;
  border: 2px solid #ffffff;
}
.hh-greet {
  flex: 1;
  min-width: 0;
}
.hh-greet-line {
  font-size: 11px;
  color: #64748b;
  line-height: 1.2;
}
.hh-name {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
  margin-top: 1px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.hh-sub {
  font-size: 11px;
  color: #475569;
  margin-top: 2px;
}
.hh-bell {
  position: relative;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: transparent;
  border: none;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex: 0 0 auto;
}
.hh-badge-count {
  position: absolute;
  top: 4px;
  right: 4px;
  background: #ef4444;
  color: #ffffff;
  font-size: 9px;
  font-weight: 700;
  padding: 1px 4px;
  border-radius: 8px;
  border: 1.5px solid #f8fafc; /* matching ambient bg */
  line-height: 1;
}

/* ----- Body ----- */
.hh-body {
  flex: 1;
  min-height: 0;
  padding: 0 16px 80px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  display: flex;
  flex-direction: column;
  gap: 16px; /* consistent spacing */
}

/* ----- Banner Carousel ----- */
.hh-banner-carousel {
  margin: 0 -16px;
  padding: 0 16px 12px;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  flex-shrink: 0;
}
.hh-banner-carousel::-webkit-scrollbar {
  display: none;
}
.hh-banner-carousel-inner {
  display: flex;
  align-items: stretch;
  gap: 12px;
  width: max-content;
}
.hh-banner-slide {
  scroll-snap-align: center;
  width: 90vw;
  max-width: 380px;
  box-sizing: border-box;
  flex-shrink: 0;
  height: 180px; /* Explicit height for webkit flex bug */
}

/* ----- Hero ----- */
.hh-hero {
  position: relative;
  display: flex;
  align-items: stretch;
  background: linear-gradient(135deg, #5b9df7 0%, #3b82f6 55%, #2563eb 100%);
  color: #ffffff;
  border-radius: 16px;
  padding: 14px 14px 20px;
  box-shadow: 0 6px 18px rgba(59, 130, 246, 0.24);
  overflow: hidden;
}
.hh-hero::before,
.hh-hero::after {
  content: "";
  position: absolute;
  border-radius: 50%;
  pointer-events: none;
}
.hh-hero::before {
  top: -60px;
  right: -50px;
  width: 180px;
  height: 180px;
  background: rgba(255, 255, 255, 0.1);
}
.hh-hero::after {
  bottom: -40px;
  right: 60px;
  width: 120px;
  height: 120px;
  background: rgba(255, 255, 255, 0.06);
}
.hh-hero-left {
  flex: 1.1;
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
}
.hh-hero-pill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: rgba(255, 255, 255, 0.22);
  font-size: 11px;
  font-weight: 500;
  padding: 4px 10px;
  border-radius: 12px;
  margin-bottom: 10px;
  align-self: flex-start;
  backdrop-filter: blur(4px);
}
.hh-pill-spark {
  font-size: 12px;
}
.hh-hero-title {
  font-size: 20px;
  font-weight: 800;
  margin: 0;
  line-height: 1.15;
  letter-spacing: -0.01em;
  white-space: nowrap;
}
.hh-hero-sub {
  font-size: 10px;
  opacity: 0.92;
  margin: 6px 0 12px;
  line-height: 1.4;
  white-space: nowrap;
}
@media (max-width: 380px) {
  .hh-hero-title {
    font-size: 18px;
  }
  .hh-hero-sub {
    font-size: 9.5px;
  }
}
.hh-hero-btn {
  background: #ffffff;
  color: #1e3a8a;
  border: none;
  padding: 9px 18px;
  border-radius: 12px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  align-self: flex-start;
  transition: transform 0.1s;
}
.hh-hero-btn:active {
  transform: scale(0.97);
}
.hh-hero-status {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 10px;
  margin-top: 12px;
  background: rgba(255, 255, 255, 0.18);
  padding: 4px 10px;
  border-radius: 10px;
  align-self: flex-start;
}
.hh-hero-status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #4ade80;
  box-shadow: 0 0 0 2px rgba(74, 222, 128, 0.3);
}
.hh-hero-right {
  flex: 0 0 auto;
  position: relative;
  z-index: 1;
  width: 110px;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: space-between;
}
.hh-hero-bubbles {
  display: flex;
  gap: 6px;
  margin-bottom: auto;
}
.hh-hero-bubble {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  backdrop-filter: blur(4px);
}
.hh-hero-doctor {
  position: relative;
  width: 96px;
  height: 96px;
  border-radius: 50%;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.85) 0%,
    rgba(219, 234, 254, 0.95) 100%
  );
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.15);
  margin-bottom: 4px;
}
.hh-hero-doctor-emoji {
  font-size: 64px;
  line-height: 1;
}
.hh-hero-doctor-wave {
  position: absolute;
  top: -6px;
  right: -6px;
  font-size: 22px;
  background: #ffffff;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
}
.hh-hero-dots {
  position: absolute;
  bottom: 8px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  gap: 4px;
  z-index: 2;
}
.hh-hero-dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.4);
  transition: width 0.2s;
}
.hh-hero-dot.active {
  width: 14px;
  border-radius: 3px;
  background: #ffffff;
}

/* ----- Service tiles Menu (4 unique shortcuts, no scroll) ----- */
.hh-tiles {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
}
.hh-tile {
  background: #ffffff;
  border: 1px solid rgba(226, 232, 240, 0.6);
  border-radius: 16px;
  padding: 14px 6px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03);
  transition: transform 0.2s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.2s;
  min-width: 0;
}
.hh-tile:hover {
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.06);
}
.hh-tile:active {
  transform: scale(0.94);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
  background: #f8fafc;
}
.hh-tile-icon {
  width: 44px;
  height: 44px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: inset 0 2px 4px rgba(255, 255, 255, 0.5);
}
.hh-tile-label {
  font-size: 11.5px;
  color: #1e293b;
  font-weight: 700;
  text-align: center;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

/* ----- Section titles ----- */
.hh-section-title {
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
  margin: 0 2px 10px;
  display: flex;
  justify-content: space-between;
  align-items: baseline;
}
.hh-section-action {
  background: transparent;
  border: none;
  color: #3b82f6;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}

/* ----- Recent ----- */
.hh-recent-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  box-shadow: none; /* Removed heavy shadow */
}
.hh-recent-avatar {
  flex: 0 0 auto;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: #eff6ff;
  display: flex;
  align-items: center;
  justify-content: center;
}
.hh-recent-info {
  flex: 1;
  min-width: 0;
}
.hh-recent-name {
  font-weight: 700;
  font-size: 14px;
  color: #0f172a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.hh-recent-spec {
  font-size: 12px;
  color: #475569;
  margin-top: 2px;
}
.hh-recent-meta {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: #64748b;
  margin-top: 4px;
}
.hh-recent-side {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
}
.hh-recent-status-pill {
  font-size: 11px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 12px;
  background: #fef3c7;
  color: #d97706;
}
.hh-recent-status-pill.status-ss {
  background: #dcfce7;
  color: #16a34a;
}
.hh-recent-btn-summary {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  color: #334155;
  font-size: 11px;
  font-weight: 600;
  padding: 6px 12px;
  border-radius: 10px;
  cursor: pointer;
}
.hh-recent-btn-summary:active {
  background: #f1f5f9;
}

/* ----- Empty ----- */
.hh-empty {
  text-align: center;
  padding: 24px 16px;
  background: #ffffff;
  border: 1px dashed #cbd5e1;
  border-radius: 16px;
  box-shadow: none;
}
.hh-empty-icon {
  font-size: 32px;
  margin-bottom: 8px;
}
.hh-empty-text {
  font-size: 14px;
  font-weight: 600;
  color: #334155;
  margin-bottom: 16px;
  line-height: 1.4;
}
.hh-empty-sub {
  font-size: 12px;
  font-weight: 400;
  color: #64748b;
}

/* ----- Promo strip ----- */
.hh-promo {
  position: relative;
  display: flex;
  align-items: center;
  padding: 12px 16px;
  background: linear-gradient(135deg, #e8f1fc 0%, #d3e6fb 100%);
  border: 1px solid #c8def8;
  border-radius: 16px;
  cursor: pointer;
  overflow: hidden;
}
.hh-promo-text {
  flex: 1;
  position: relative;
  z-index: 1;
}
.hh-promo-pill {
  display: inline-block;
  background: #ffffff;
  color: #2563eb;
  font-size: 11px;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 6px;
  margin-bottom: 12px;
}
.hh-promo-title {
  font-size: 16px;
  font-weight: 800;
  color: #1e3a8a;
  margin: 0 0 6px;
}
.hh-promo-tagline {
  font-size: 12px;
  color: #475569;
}
.hh-promo-illu {
  flex: 0 0 auto;
  width: 130px;
  height: 100px;
  position: relative;
  z-index: 1;
  margin-right: -10px;
}
</style>
